import React, { useEffect, useRef, useState } from 'react';
import './filterTemplate.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import {
  getFilterData,
  getJsonFromStorage,
  getPaginationOpts, getResponseError,
  responseLogWithNotification, useSort,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getFilterTemplateData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { DRIVER_LOG_MODULE } from '../../../configs/moduleNames';
import {
  updateFilterTemplate,
  deleteFilterTemplate,
  loadFilterTemplateByIdClear,
  loadFilterTemplateList, loadFilterTemplateById,
} from '../actions';
import { columnSettings } from './settings';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Icons, Button } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInput from '../../../components/fields/TextInput/TextInput';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import FilterTemplateView from './FilterTemplateView';
import FilterTemplateForm from './FilterTemplateForm';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import CustomSelectInputWithHelper from '../../../components/fields/SelectInput/CustomSelectInputWithHelper';
import { loadUserList } from '../../User/actions';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import { FILTER_TEMPLATE_STATUS_LIST } from '../../../configs';
import FilterTemplateDTO from '../../../dto/FilterTemplateDTO';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import { PAGE_TYPE_LIST } from './filter-settings';
import TimerLoader from '../../../components/base/Loader/TimerLoader';

export default function FilterTemplatePage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const {
    filterTemplateList,
    loadingFilterTemplateList,
    filterTemplateListMeta,
    loadingFilterTemplate,
    loadingCreateFilterTemplate,
    loadingUpdateFilterTemplate,
  } = useSelector(getFilterTemplateData);
  const filterName = 'filter-template-filter';
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(null);

  const { formatMessage } = useIntl();
  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[DRIVER_LOG_MODULE].filterTemplateListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadFilterTemplateList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [sort, changeSort] = useSort(columnSettings);

  useEffect(() => { loadData(); }, [sort]);

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  const defaultFilterValues = {
    search: '', created_at: '',
  };

  useEffect(() => reset(getFilterData(filterName, defaultFilterValues)), []);
  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getJsonFromStorage(filterName, defaultFilterValues) });

  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleClose = () => {
    setSelectedId(null);
    setShowForm(false);
    setShowView(null);
    clearCurrentState();
  };

  const handleCreate = () => {
    clearCurrentState();
    setShowForm(true);
  };

  const clearCurrentState = () => {
    dispatch(loadFilterTemplateByIdClear());
  };

  const handleView = item => event => {
    setShowView(item);
  };

  const handleCloseView = event => {
    setShowView(null);
  };

  const handleEdit = item => event => {
    dispatch(loadFilterTemplateById(item.id))
      .then(() => {
        setShowForm(true);
      })
      .catch(response => errorNotification('Загрузка шаблона', getResponseError(response)));
  };

  const handleArchive = item => event => {
    if (!item) {
      return;
    }
    const title = item.is_active ? 'Архивация шаблона' : 'Извлечение шаблона из архива';
    const successMessage = item.is_active ? 'Архивация прошла успешно' : 'Извлечение шаблона из архива прошло успешно';
    dispatch(updateFilterTemplate({ ...item, is_active: !item.is_active }))
      .then(() => {
        loadData();
        successNotification(title, successMessage);
      })
      .catch(response => errorNotification(title, getResponseError(response)));
  };

  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление шаблона ${item.name || ''}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteFilterTemplate(item.id)).then(() => {
        if (selectedId === item.id) {
          setSelectedId(null);
        }
        loadData();
        successNotification('Удаление обращения', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление обращения', getResponseError(response));
      });
    }).catch(() => {});
    return false;
  };

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button variant="link" className="w-100 more-button" onClick={handleView(item)}>
        Посмотреть
      </Button>
      <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
        Редактировать
      </Button>
      <Button variant="link" className="w-100 more-button" onClick={handleArchive(item)}>
        {!item.is_active ? 'Вернуть из архива' : 'Переместить в архив'}
      </Button>
      <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
        Удалить
      </Button>
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );
  const DTO = new FilterTemplateDTO();
  const attributes = DTO.getAttributesByName();
  return (
    <div className="page">
      <TimerLoader global loading={loadingFilterTemplateList || loadingFilterTemplate || loadingCreateFilterTemplate || loadingUpdateFilterTemplate} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.filter-template' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.search}
            className="no-border"
            inputBefore={(
              <div className="search-wrapper">
                <Icons customIcon className="search" width={24} height={24} icon="search" />
              </div>
            )}
          />
          <FieldDTO
            control={control}
            dtoObject={attributes.causer_id}
            usePortal
            className="no-border"
            addPlaceholder
            placeholder="Автор"
            component={CustomSelectInputWithHelper}
            portalClassName="executor-selector"
            loadAction={loadUserList}
            optionFormatter={item => ({ value: item.id, name: item.full_name })}
            filterName="full_name"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.page}
            usePortal
            placeholder="Раздел"
            className="no-border"
            options={PAGE_TYPE_LIST}
            addPlaceholder
            multiple
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.is_active}
            usePortal
            placeholder="Статус"
            className="no-border"
            options={FILTER_TEMPLATE_STATUS_LIST}
            addPlaceholder
            multiple
          />
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={attributes.created_at}
            usePortal
            placeholder="Дата создания"
            changeFormat="YYYY-MM-DD"
            className="no-border"
          />
          <div className="filter-sep" />
          <Button variant="second" onClick={handleCreate}>
            <Icons
              customIcon
              className="file-download"
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={filterTemplateList}
                columns={columnSettings}
                name="driver-list"
                changeSort={changeSort}
                sort={sort}
                additionalColumns={[]}
                onDoubleClickByRow={(item, event) => handleView(item)(event)}
                settingsFormatter={moreFormatter}
                renderContextMenu={renderContextMenu}
                rowClassFormatter={item => 'clickable-table-row'}
              />
            </div>
          </div>
          <Pagination meta={filterTemplateListMeta} list={filterTemplateList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showView && showView ? <FilterTemplateView onClose={handleCloseView} item={showView} handleEdit={handleEdit} handleArchive={handleArchive} /> : null}
      {showForm ? <FilterTemplateForm onClose={handleClose} id={selectedId} loadData={loadData} handleView={handleView} handleCloseView={handleCloseView} /> : null}
    </div>
  );
}
